// src/purchases/pdfview.js — фактурата като изображение, с ограден ред.
//
// Макс, 29.10.2026: „да оцветява реда, който коригираш, на фактурата — откъде
// точно го е взело“. Вграденият PDF преглед на браузъра не позволява да се
// рисува върху страницата, затова страниците се рисуват тук (pdf.js), а над
// тях стои рамка на мястото на реда. Мястото идва от OCR-а:
// line.ocr_box = [страница, x0, y0, x1, y1] в части от страницата (0..1).
//
// Над изображението стои невидим текст на същите места — маркира се с мишката
// и се копира (Ctrl+C) (Макс, 29.10.2026). Думите идват от OCR-а
// (doc.ocr.words = [[страница, x0, y0, x1, y1, текст], …]); ако ги няма, а PDF-ът
// има истински текст — от самия PDF.
//
// pdf.js се зарежда чак при нужда (отделен файл), не с цялото приложение.
// Където няма canvas (тестовете в jsdom) или зареждането пропадне, show()
// връща false и екранът остава на обикновения PDF преглед.

const MAX_PAGES = 12;

export function canvasOk(doc) {
  try {
    const c = (doc || document).createElement('canvas');
    return !!(c && c.getContext && c.getContext('2d'));
  } catch (e) {
    return false;
  }
}

let libPromise = null;
function lib() {
  if (!libPromise) {
    libPromise = Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]).then(([pdfjs, worker]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs;
    });
  }
  return libPromise;
}

// Невидимият текст на една страница: всяка дума на мястото си, разтеглена до
// ширината си, за да съвпада маркираното с видимото.
function textLayer(doc, wrap, words) {
  if (!words.length) return;
  const layer = doc.createElement('div');
  layer.className = 'pur-text';
  const h = wrap.clientHeight || 1;
  const spans = [];
  for (const [x0, y0, x1, y1, text] of words) {
    const sp = doc.createElement('span');
    sp.textContent = text + ' ';
    Object.assign(sp.style, {
      left: x0 * 100 + '%', top: y0 * 100 + '%',
      fontSize: Math.max(4, (y1 - y0) * h * 0.92) + 'px',
      lineHeight: Math.max(4, (y1 - y0) * h) + 'px',
    });
    layer.appendChild(sp);
    spans.push([sp, (x1 - x0)]);
  }
  wrap.appendChild(layer);
  const w = wrap.clientWidth || 1;
  for (const [sp, frac] of spans) {
    const natural = sp.getBoundingClientRect().width;
    if (natural > 0) sp.style.transform = 'scaleX(' + ((frac * w) / natural).toFixed(3) + ')';
  }
}

// Думите от текста на самия PDF (електронна фактура) — същият вид като от OCR-а.
async function pdfWords(pdfjs, page) {
  const vp = page.getViewport({ scale: 1 });
  const tc = await page.getTextContent();
  const out = [];
  for (const it of tc.items || []) {
    if (!it.str || !it.str.trim()) continue;
    const t = pdfjs.Util.transform(vp.transform, it.transform);
    const fh = Math.hypot(t[2], t[3]);
    const x0 = t[4] / vp.width;
    const y0 = (t[5] - fh) / vp.height;
    const x1 = x0 + (it.width || 0) / vp.width;
    const y1 = t[5] / vp.height;
    if (x1 > x0 && y1 > y0) out.push([x0, y0, x1, y1, it.str]);
  }
  return out;
}

// Рисува PDF-а от url в box (ширина = ширината на box). → { highlight(box|null) }
export async function render(box, url, words) {
  const pdfjs = await lib();
  // без eval — политиката за сигурност (CSP) на CRM-а не го позволява
  const pdf = await pdfjs.getDocument({ url, withCredentials: true, isEvalSupported: false }).promise;
  const doc = box.ownerDocument;
  const pages = [];
  const width = Math.max(320, box.clientWidth - 4);
  const ratio = Math.min(2, (doc.defaultView && doc.defaultView.devicePixelRatio) || 1);
  for (let n = 1; n <= Math.min(pdf.numPages, MAX_PAGES); n++) {
    const page = await pdf.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const vp = page.getViewport({ scale: (width / base.width) * ratio });
    const wrap = doc.createElement('div');
    wrap.className = 'pur-page';
    const canvas = doc.createElement('canvas');
    canvas.width = Math.floor(vp.width);
    canvas.height = Math.floor(vp.height);
    canvas.style.width = '100%';
    canvas.setAttribute('aria-hidden', 'true');
    const mark = doc.createElement('div');
    mark.className = 'pur-hl';
    mark.hidden = true;
    wrap.append(canvas, mark);   // текстът се слага след рисуването — над рамката
    box.appendChild(wrap);
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    let pw = (Array.isArray(words) ? words : []).filter((x) => x[0] === n - 1).map((x) => x.slice(1));
    if (!pw.length) { try { pw = await pdfWords(pdfjs, page); } catch (e) { pw = []; } }
    textLayer(doc, wrap, pw);
    pages.push({ wrap, mark });
  }
  let shown = null;
  return {
    pages: pages.length,
    highlight(b) {
      if (shown) { shown.hidden = true; shown = null; }
      if (!Array.isArray(b) || b.length !== 5) return false;
      const p = pages[b[0]];
      if (!p) return false;
      const pad = 0.004;
      Object.assign(p.mark.style, {
        left: Math.max(0, b[1] - pad) * 100 + '%',
        top: Math.max(0, b[2] - pad * 2) * 100 + '%',
        width: (Math.min(1, b[3] + pad) - Math.max(0, b[1] - pad)) * 100 + '%',
        height: (Math.min(1, b[4] + pad * 2) - Math.max(0, b[2] - pad * 2)) * 100 + '%',
      });
      p.mark.hidden = false;
      shown = p.mark;
      // редът да е в средата на панела, без да мести целия диалог
      const top = p.wrap.offsetTop + p.wrap.clientHeight * b[2] - box.clientHeight / 2;
      box.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      return true;
    },
  };
}
